import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { registerUser, ErrorType, getClubTerms } from "../api";
import ClubTermsModal from "../components/ClubTermsModal";

function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [clubTerms, setClubTerms] = useState({
    enabled: false,
    enforceOnRegistration: false,
    html: ''
  });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);

  useEffect(() => {
    const fetchClubTerms = async () => {
      try {
        const terms = await getClubTerms();
        setClubTerms({
          enabled: terms.enabled ?? false,
          enforceOnRegistration: terms.enforceOnRegistration ?? false,
          html: terms.html || ''
        });
      } catch (err) {
        console.warn('Unable to load club terms, falling back to defaults', err);
        setClubTerms({
          enabled: false,
          enforceOnRegistration: false,
          html: ''
        });
      } finally {
        setIsLoadingTerms(false);
      }
    };

    fetchClubTerms();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const validateForm = () => {
    if (!form.firstName || !form.lastName || !form.email || !form.password || !form.confirmPassword) {
      setError("All fields are required");
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    // Basic email validation - trim email before validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError("Please enter a valid email address");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    // Only enforce terms acceptance if enforcement is enabled
    if (clubTerms.enforceOnRegistration && !hasAcceptedTerms) {
      setError("Please review and accept the club terms to continue.");
      setShowTermsModal(true);
      return;
    }

    setIsLoading(true);

    try {
      const response = await registerUser({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password
      });
      
      // Redirect to login with a success message
      navigate("/login", { state: { message: response.message } });
    } catch (err) {
      switch (err.type) {
        case ErrorType.VALIDATION:
          setError(err.message);
          break;
        case ErrorType.NETWORK:
          setError('Unable to connect to the server. Please check your internet connection and try again.');
          break;
        case ErrorType.SERVER:
          setError('The server encountered an error. Please try again later.');
          break;
        default:
          setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="relative flex items-center mb-6">
          <h2 className="w-full text-center text-2xl font-bold text-slate-800">Register</h2>
        </div>
        <p className="text-slate-600 text-center mb-6">Please fill in your details to register</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-slate-600 mb-1">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                value={form.firstName}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-slate-600 mb-1">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                value={form.lastName}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-600 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="text"
              name="email"
              autoComplete="email"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              value={form.email}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-600 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              value={form.password}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-600 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 rounded-md text-white font-medium transition-all duration-200 ${
              isLoading 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            } flex items-center justify-center`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Registering...
              </>
            ) : (
              'Register'
            )}
          </button>
        </form>

        {clubTerms.enforceOnRegistration && (
          <p className="mt-6 text-center text-sm text-slate-600">
            To register, you will need to{' '}
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="font-medium text-blue-600 hover:text-blue-500 transition"
            >
              Accept Club Terms
            </button>
          </p>
        )}
        
        <p className="mt-6 text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link 
            to="/login" 
            className="font-medium text-blue-600 hover:text-blue-500 transition"
          >
            Sign in instead
          </Link>
        </p>
      </div>

      <ClubTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        htmlContent={clubTerms.html}
        title="Club Terms"
        showAccept={clubTerms.enforceOnRegistration}
        acceptLabel="Accept"
        onAccept={() => {
          setHasAcceptedTerms(true);
          setShowTermsModal(false);
          setError("");
        }}
      />
    </div>
  );
}

export default Register;
